import React, { useCallback, useEffect, useState } from 'react';
import { Field, change as changeFieldValue } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { Row, Col, Input } from 'reactstrap';
import { FormGroup, Label } from 'reactstrap';
import DateTimePicker from '../DateTimePicker/DateTimePicker';
import DurationField from '../DurationField/DurationField';
import OverlapField from '../OverlapField/OverlapField';
import ScreeningsField from '../ScreeningsField/ScreeningsField';
import { EMPTY_OBJECT, Utils } from '../../services';
import { Jumbotron } from 'reactstrap';
import HelpTooltips from '../HelpTooltips/HelpTooltips';

import './WindowForm.css';

const MODE_DATE_RANGE = 'date_range';
const MODE_DURATION = 'duration';
const MODE_SCREENING = 'screening';

const WindowForm = ({ window, idx, screenings }) => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();
    const formWindow = useSelector(state => state.form?.tickettypeForm?.values?.windows?.[idx] || EMPTY_OBJECT);
    const getMode = (w) => {
        if (w?.screening_id)
            return MODE_SCREENING;
        if (w?.duration)
            return MODE_DURATION;
        if (w?.start_at || w?.stop_at)
            return MODE_DATE_RANGE;
        return MODE_DATE_RANGE;
    };
    const [activeMode, setMode] = useState(getMode(window));

    const predefinedDurations = Utils.durations().map(d => ({
        text: t(`common.duration.${d}`),
        value: d
    }));
    const nbookingsValue = parseInt(formWindow?.nbookings ?? window?.nbookings, 10) || 0;
    const sameDayFieldId = `window_${idx}_same_day`;

    const setWindowField = useCallback(
        (field, value) => {
            dispatch(changeFieldValue('tickettypeForm', `windows[${idx}].${field}`, value));
        },
        [dispatch, idx]
    );

    const onModeChange = (mode) => {
        setMode(mode);
        if (mode === MODE_DATE_RANGE) {
            setWindowField('duration', null);
            setWindowField('screening_id', null);
        } else if (mode === MODE_DURATION) {
            setWindowField('start_at', null);
            setWindowField('stop_at', null);
            setWindowField('screening_id', null);
        } else if (mode === MODE_SCREENING) {
            setWindowField('start_at', null);
            setWindowField('stop_at', null);
            setWindowField('duration', null);
        }
    };

    useEffect(() => {
        if (nbookingsValue <= 1 && formWindow?.nsame_screening != null) {
            setWindowField('nsame_screening', null);
        }
    }, [formWindow.nsame_screening, nbookingsValue, setWindowField]);

    return (
        <div className="WindowForm">
            <Jumbotron>
                <h6>{t("tickettypes.window.events_section_title")}</h6>
                <Row>
                    <Col sm={12}>
                        <FormGroup row>
                            <Label for={`window_mode_${idx}`} sm={12}>{t("tickettypes.window.events_mode")}</Label>
                            <Col sm={12}>
                                <Input
                                    id={`window_mode_${idx}`}
                                    type="select"
                                    value={activeMode}
                                    onChange={e => onModeChange(e.target.value)}
                                >
                                    <option value={MODE_DATE_RANGE}>{t("tickettypes.window.events_mode_dates")}</option>
                                    <option value={MODE_DURATION}>{t("tickettypes.window.events_mode_duration")}</option>
                                    <option value={MODE_SCREENING}>{t("tickettypes.window.events_mode_screening")}</option>
                                </Input>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>

                {activeMode === MODE_DATE_RANGE && (
                <Row>
                    <Col sm={12} md={6}>
                        <FormGroup row>
                            <Label for="start_at" sm={12}>{t("tickettypes.window.start_at")}</Label>
                            <Col sm={12}>
                                <Field
                                    component={DateTimePicker}
                                    type="text"
                                    name={`windows[${idx}].start_at`}
                                    timeFormat={true}
                                    maxBoundField={`windows[${idx}].stop_at`}
                                />
                            </Col>
                        </FormGroup>
                    </Col>
                    <Col sm={12} md={6}>
                        <FormGroup row>
                            <Label for="stop_at" sm={12}>{t("tickettypes.window.stop_at")}</Label>
                            <Col sm={12}>
                                <Field
                                    component={DateTimePicker}
                                    type="text"
                                    name={`windows[${idx}].stop_at`}
                                    timeFormat={true}
                                    endOfDayIfMidnight
                                    minBoundField={`windows[${idx}].start_at`}
                                />
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
                )}

                {activeMode === MODE_DURATION && (
                <Row>
                    <Col sm={12}>
                        <FormGroup row>
                            <Label for="duration" sm={12}>{t("tickettypes.window.duration_after_purchase")}</Label>
                            <Col sm={12}>
                                <DurationField
                                    name={`windows[${idx}].duration`}
                                    predefinedValues={ predefinedDurations }
                                    onlyParts={['Y', 'M', 'D', 'H', 'X']}
                                    data-tip
                                    data-for={`durationTip.${idx}`}
                                />
                                <HelpTooltips id={`durationTip.${idx}`} value='duration'/>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
                )}

                {activeMode === MODE_SCREENING && (
                <Row>
                    <Col sm={12}>
                        <FormGroup row>
                            <Label for="screening_id" sm={12}>{t("common.screening", { count: 1 })}</Label>
                            <Col sm={12}>
                                <div data-tip data-for={`screeningIdTip.${idx}`}>
                                    <ScreeningsField
                                        name={`windows[${idx}].screening_id`}
                                        screenings={screenings}
                                        placeholder={t("common.screening", { count: 1 })}
                                        single
                                    />
                                </div>
                                <HelpTooltips id={`screeningIdTip.${idx}`} value='screening_id'/>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
                )}
            </Jumbotron>
            <Jumbotron>
                <h6>{t("tickettypes.window.capacity_section_title")}</h6>
                <Row>
                    <Col>
                        <FormGroup row>
                            <Label for="nbookings" sm={12}>{t("tickettypes.window.nbookings")}</Label>
                            <Col sm={12}>
                                <Field data-tip data-for={`reservationNumTip.${idx}`} component="input" type="text" className="form-control" name={`windows[${idx}].nbookings`} />
                                <HelpTooltips id={`reservationNumTip.${idx}`} value='reservation_number'/>
                            </Col>
                        </FormGroup>
                    </Col>
                    <Col className={nbookingsValue > 1 ? undefined : 'd-none'} aria-hidden={nbookingsValue <= 1}>
                        <FormGroup row>
                            <Label for="nsame_screening" sm={12}>{t("tickettypes.window.nsame_screening")}</Label>
                            <Col sm={12}>
                                <Field data-tip data-for={`sameScreeningTip.${idx}`} component="input" type="text" className="form-control" name={`windows[${idx}].nsame_screening`} tabIndex={nbookingsValue > 1 ? undefined : -1} />
                                <HelpTooltips id={`sameScreeningTip.${idx}`} value='same_screening'/>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
            </Jumbotron>
            <Jumbotron>
                <h6>{t("tickettypes.window.options_section_title")}</h6>
                <Row>
                    <Col>
                        <FormGroup row>
                            <Label for="overlap" sm={12}>{t("tickettypes.window.overlap")}</Label>
                            <Col sm={12}>
                                <OverlapField
                                    name={`windows[${idx}].overlap`}
                                    defaultValue={window.overlap}
                                    placeholder="PT0M"
                                    predefinedValues={ predefinedDurations }
                                    onlyParts={['D', 'H', 'X']}
                                    data-tip
                                    data-for={`overlapTip.${idx}`}
                                />
                                <HelpTooltips id={`overlapTip.${idx}`} value='overlap'/>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
                <FormGroup className="mb-0">
                    <div className="d-flex align-items-center justify-content-start text-left">
                        <Field
                            id={sameDayFieldId}
                            data-tip
                            data-for={`sameDayTip.${idx}`}
                            component="input"
                            type="checkbox"
                            className="mr-2"
                            name={`windows[${idx}].same_day`}
                            format={v => { return !!v}}
                        />
                        <Label for={sameDayFieldId} className="mb-0 text-left">
                            {t("tickettypes.window.same_day_all_events")}
                        </Label>
                    </div>
                    <HelpTooltips id={`sameDayTip.${idx}`} value='same_day'/>
                </FormGroup>
            </Jumbotron>
        </div>
    );
}

export default WindowForm;
